import { clsx } from 'clsx';
import { FC, ReactNode } from 'react';

import { styled } from '@mui/material/styles';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system/createStyled';
import Box from '@o2/ui/Box';
import Stack, { StackProps } from '@o2/ui/Stack';
import { BaseBackgroundColorList } from '@o2/ui/theme/colorList';
import Typography, { TypographyProps } from '@o2/ui/Typography';

import { useFieldListItemUtilityClasses } from '../fieldListClasses';
import EditControls from './EditControls';

export interface FieldListItemProps extends StackProps {
  /**
   * Item background color
   */
  color?: BaseBackgroundColorList;

  /**
   * Item label
   */
  label: ReactNode;

  /**
   * Item label width in percent
   */
  labelWidthPercent?: number;

  /**
   * Item value
   */
  value: ReactNode;

  /**
   * Value align
   */
  valueAlign?: TypographyProps['textAlign'];

  /**
   * Disable styling the passed `value`. This prop has no effect if `editing` is true: `editValue` is never styled.
   *
   * Defaults to false (= does style) if `value` is a string,
   * true otherwise (if you pass a ReactElement, for example, it will not be styled).
   *
   * Useful to override for example when composing the text content from several `<span/>`s.
   */
  disableValueStyling?: boolean;

  /**
   * Switch between show and edit mode
   */
  editing?: boolean;

  /**
   * Value or form displayed in edit mode.
   *
   * The `disableValueStyling` prop has no effect on the value passed here (it is never styled).
   */
  editValue?: ReactNode;

  /**
   * Controls - buttons, links
   */
  controls?: ReactNode;

  /**
   * Controls displayed in edit mode - buttons, links
   */
  editControls?: ReactNode;

  /**
   * Orientation breakpoints, you can switch between column and row dynamically by breakpoints
   */
  orientation?: Exclude<StackProps['direction'], Array<unknown>>;

  /**
   * Control display position - right or bottom
   */
  controlsPosition?: 'right' | 'bottom';

  'data-elm'?: string;
}

const StyledBox = styled(Box, {
  name: 'O2FieldListItem',
  slot: 'root',
  shouldForwardProp: (propName) => sysShouldForwardProp(propName) && propName !== 'color',
})<{ color?: Pick<FieldListItemProps, 'color'> }>(({ theme, color }) => ({
  backgroundColor: color === 'internal' ? theme.palette.background.internal : undefined,
}));

interface FieldListItem extends FC<FieldListItemProps> {
  EditControls: typeof EditControls;
}

const defaultOrientation: FieldListItemProps['orientation'] = { xs: 'column', sm: 'row' };

const FieldListItem: FieldListItem = ({
  classes,
  color,
  label,
  labelWidthPercent = 40,
  value,
  valueAlign = 'left',
  disableValueStyling = typeof value !== 'string',
  editing,
  editValue,
  controls,
  editControls,
  orientation = defaultOrientation,
  controlsPosition = 'right',
  ...other
}) => {
  const utilityClasses = useFieldListItemUtilityClasses({ classes, orientation });

  return (
    <StyledBox color={color} className={clsx(utilityClasses.item)}>
      <Stack direction={orientation} justifyContent="space-between" {...other}>
        <Typography
          component="div"
          flexBasis={`${labelWidthPercent}%`}
          fontWeight="600"
          className={clsx(utilityClasses.cell, utilityClasses.label)}
        >
          {label}
        </Typography>
        <Box flexBasis={`${100 - labelWidthPercent}%`} className={clsx(utilityClasses.cell, utilityClasses.value)}>
          <Stack direction={controlsPosition === 'right' ? orientation : 'column'} gap={1.5}>
            <Stack flexGrow={editing ? undefined : 1} direction="row" justifyContent={editing ? 'start' : valueAlign}>
              {editing ? (
                editValue
              ) : disableValueStyling ? (
                value
              ) : (
                <Typography component="div" color="textSecondary" textAlign={valueAlign}>
                  {value}
                </Typography>
              )}
            </Stack>
            {(controls || editControls) && <Box>{editing ? editControls : controls}</Box>}
          </Stack>
        </Box>
      </Stack>
    </StyledBox>
  );
};

FieldListItem.EditControls = EditControls;
FieldListItem.displayName = 'FieldList.Item';

export default FieldListItem;
